import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { splitItem } from "../../vendor/src/features/remotion-editor/editor/state/actions/split-item";
import { trimClipLeft } from "../../vendor/src/features/remotion-editor/editor/state/actions/trim-clip-left";
import { trimClipRight } from "../../vendor/src/features/remotion-editor/editor/state/actions/trim-clip-right";
import { itemOwner } from "./group-split";
import { removeTimelineTime, type TimeRange } from "./timeline-ripple";

export type TimelineEdit = "split" | "trim-left" | "trim-right";

/** Timeline intent, not transcript word editing. Only a main visual selection initiates sync. */
export function timelineEditTargets(
  state: EditorState,
  selected: readonly string[],
  frame: number,
): string[] {
  if (!Number.isInteger(frame)) return [];
  const { tracks, items } = state.undoableState;
  const eligible = (id: string) => {
    const item = items[id];
    const track = itemOwner(state, id)?.track;
    return (
      !!item &&
      !!track &&
      !track.locked &&
      frame > item.from &&
      frame < item.from + item.durationInFrames
    );
  };
  const targets = new Set(selected.filter(eligible));
  if (!state.isLinkingEnabled) return [...targets];
  const hasMain = [...targets].some((id) =>
    tracks.some(
      (track) =>
        track.items.includes(id) &&
        track.metadata?.isMainTrack &&
        track.metadata.type === "video",
    ),
  );
  if (hasMain) {
    for (const track of tracks) {
      // Narration and captions participate in a main cut. Persistent graphics/BGM do not acquire needless cuts.
      if (
        track.locked ||
        !track.metadata?.isMainTrack ||
        !["caption", "audio"].includes(track.metadata.type) ||
        track.metadata.kind === "bgm"
      )
        continue;
      for (const id of track.items) if (eligible(id)) targets.add(id);
    }
  }
  return [...targets];
}

/** All keyboard/button entry points use the exact cursor frame, never an implicit word-boundary snap. */
export function applyTimelineEdit(
  state: EditorState,
  selected: readonly string[],
  frame: number,
  operation: TimelineEdit,
): EditorState {
  const targets = timelineEditTargets(state, selected, frame);
  if (operation !== "split") {
    const origins = targets.filter((id) => selected.includes(id));
    const magnetic = origins.filter((id) => {
      const owner = itemOwner(state, id);
      return owner && !owner.parentId && owner.track.magnetic;
    });
    const main = magnetic.filter(
      (id) =>
        itemOwner(state, id)?.track.metadata?.type === "video" &&
        itemOwner(state, id)?.track.metadata?.isMainTrack,
    );
    const rangesFor = (ids: string[]): TimeRange[] =>
      ids.map((id) => {
        const item = state.undoableState.items[id]!;
        return operation === "trim-left"
          ? { start: item.from, end: frame }
          : { start: frame, end: item.from + item.durationInFrames };
      });
    // When a main edit owns the global time change, co-selected companions must not remove it twice.
    if (state.isLinkingEnabled && main.length) {
      const result = removeTimelineTime(
        { ...state, selectedItems: origins },
        state.undoableState.tracks.map((track) => track.id),
        rangesFor(main),
      );
      return result.ok ? result.state : state;
    }
    if (magnetic.length) {
      let changed = { ...state, selectedItems: origins };
      for (const track of state.undoableState.tracks) {
        const ids = magnetic.filter((id) => track.items.includes(id));
        if (!ids.length) continue;
        const result = removeTimelineTime(changed, [track.id], rangesFor(ids));
        if (!result.ok) return state;
        changed = result.state;
      }
      for (const id of origins.filter((id) => !magnetic.includes(id))) {
        changed =
          operation === "trim-left"
            ? trimClipLeft({ state: changed, itemId: id, framePosition: frame })
            : trimClipRight({
                state: changed,
                itemId: id,
                framePosition: frame,
              });
      }
      return changed;
    }
  }
  let next = state;
  const selection: string[] = [];
  for (const itemId of targets) {
    const updated =
      operation === "split"
        ? splitItem({ state: next, idToSplit: itemId, framePosition: frame })
        : operation === "trim-left"
          ? trimClipLeft({ state: next, itemId, framePosition: frame })
          : trimClipRight({ state: next, itemId, framePosition: frame });
    if (updated !== next) selection.push(...updated.selectedItems);
    next = updated;
  }
  return next === state
    ? state
    : { ...next, selectedItems: [...new Set(selection)] };
}
